import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  Rating,
  Chip,
  LinearProgress,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { keyframes } from '@mui/system';

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

export const TopPerformersSection = () => {
  const topPerformers = [
    {
      name: 'John Doe',
      department: 'Sales',
      rating: 5,
      achievements: 156,
      growth: '+24%',
      rank: 1,
    },
    {
      name: 'Jane Smith',
      department: 'Marketing',
      rating: 5,
      achievements: 142,
      growth: '+21%',
      rank: 2,
    },
    {
      name: 'Alice Johnson',
      department: 'Technology',
      rating: 4.5,
      achievements: 138,
      growth: '+19%',
      rank: 3,
    },
    {
      name: 'Robert Brown',
      department: 'Operations',
      rating: 4.5,
      achievements: 127,
      growth: '+17%',
      rank: 4,
    },
    {
      name: 'Emily Davis',
      department: 'Customer Success',
      rating: 4.5,
      achievements: 124,
      growth: '+16%',
      rank: 5,
    },
    {
      name: 'Michael Wilson',
      department: 'Finance',
      rating: 4,
      achievements: 118,
      growth: '+15%',
      rank: 6,
    },
    {
      name: 'Sarah Miller',
      department: 'HR',
      rating: 4,
      achievements: 112,
      growth: '+14%',
      rank: 7,
    },
    {
      name: 'David Anderson',
      department: 'Analytics',
      rating: 4,
      achievements: 109,
      growth: '+13%',
      rank: 8,
    },
    {
      name: 'Laura Thomas',
      department: 'Legal',
      rating: 4,
      achievements: 105,
      growth: '+12%',
      rank: 9,
    },
    {
      name: 'Chris Moore',
      department: 'Growth',
      rating: 4,
      achievements: 101,
      growth: '+11%',
      rank: 10,
    },
  ];

  const getMedalColor = (rank) => {
    switch (rank) {
      case 1:
        return {
          main: '#FFD700',
          light: '#FFF8DC',
          dark: '#DAA520',
          shadow: 'rgba(255, 215, 0, 0.25)',
        };
      case 2:
        return {
          main: '#C0C0C0',
          light: '#F5F5F5',
          dark: '#A9A9A9',
          shadow: 'rgba(192, 192, 192, 0.25)',
        };
      case 3:
        return {
          main: '#CD7F32',
          light: '#F4E4D7',
          dark: '#B8733F',
          shadow: 'rgba(205, 127, 50, 0.25)',
        };
      default:
        return {
          main: '#667eea',
          light: '#e8ebff',
          dark: '#5568d3',
          shadow: 'rgba(102, 126, 234, 0.25)',
        };
    }
  };

  return (
    <Box sx={{ py: 10, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              mb: 3,
              animation: `${float} 3s ease-in-out infinite`,
              boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 50, color: '#fff' }} />
          </Box>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800, 
              color: '#1a1a2e',
              mb: 2,
              letterSpacing: '-0.5px',
            }}
          >
            Top 10 Performers
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#64748b',
              fontWeight: 400,
            }}
          >
            Celebrating Excellence & Outstanding Contributions
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {topPerformers.map((performer) => {
            const colors = getMedalColor(performer.rank);
            const isTopThree = performer.rank <= 3;
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={performer.rank}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    pt: 4.5,
                    textAlign: 'center',
                    borderRadius: 4,
                    height: '100%',
                    minWidth: { xs: '100%', sm: '220px', md: '240px', lg: '260px' },
                    maxWidth: { xs: '100%', sm: '100%', md: '100%', lg: '300px' },
                    position: 'relative',
                    background: '#ffffff',
                    border: isTopThree ? `3px solid ${colors.main}` : '2px solid #e2e8f0',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'visible',
                    mx: 'auto',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 40px ${colors.shadow}`,
                      borderColor: colors.main,
                    },
                    '&::before': isTopThree ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(90deg, transparent, ${colors.light}, transparent)`,
                      animation: `${shimmer} 3s infinite`,
                    } : {},
                  }}
                >
                  {/* Decorative corner accent */}
                  {isTopThree && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 0,
                        height: 0,
                        borderStyle: 'solid',
                        borderWidth: '0 50px 50px 0',
                        borderColor: `transparent ${colors.main} transparent transparent`,
                        opacity: 0.2,
                      }}
                    />
                  )}

                  {/* Rank Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -28,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${colors.main} 0%, ${colors.dark} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '5px solid #ffffff',
                      boxShadow: `0 10px 25px ${colors.shadow}`,
                      zIndex: 10,
                    }}
                  >
                    {isTopThree ? (
                      <WorkspacePremiumIcon 
                        sx={{ 
                          fontSize: 34, 
                          color: '#fff',
                        }} 
                      />
                    ) : (
                      <Typography
                        variant="h5"
                        sx={{ 
                          fontWeight: 800, 
                          color: '#fff',
                          fontSize: '1.3rem',
                        }}
                      >
                        {performer.rank}
                      </Typography>
                    )}
                  </Box>

                  {/* Avatar */}
                  <Box
                    sx={{
                      mt: 2,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 90,
                        height: 90,
                        background: `linear-gradient(135deg, ${colors.main} 0%, ${colors.dark} 100%)`,
                        color: '#fff',
                        fontSize: '2.5rem',
                        fontWeight: 700,
                        boxShadow: `0 10px 30px ${colors.shadow}`,
                        border: '5px solid #f8fafc',
                      }}
                    >
                      {performer.name.charAt(0)}
                    </Avatar>
                  </Box>

                  {/* Name */}
                  <Typography
                    variant="h6"
                    sx={{ 
                      fontWeight: 700, 
                      color: '#1e293b',
                      mb: 1,
                      fontSize: '1.05rem',
                      lineHeight: 1.3,
                    }}
                  >
                    {performer.name}
                  </Typography>

                  {/* Department */}
                  <Chip
                    label={performer.department}
                    size="small"
                    sx={{
                      mb: 2.5,
                      background: `linear-gradient(135deg, ${colors.main} 0%, ${colors.dark} 100%)`,
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      height: 26,
                      borderRadius: '13px',
                      boxShadow: `0 2px 8px ${colors.shadow}`,
                      '& .MuiChip-label': {
                        px: 2,
                      },
                    }}
                  />

                  {/* Rating */}
                  <Box sx={{ mb: 3 }}>
                    <Rating 
                      value={performer.rating} 
                      precision={0.5} 
                      readOnly 
                      size="small"
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: colors.main,
                          filter: `drop-shadow(0 2px 4px ${colors.shadow})`,
                        },
                        '& .MuiRating-iconEmpty': {
                          color: '#cbd5e1',
                        },
                      }}
                    />
                  </Box>

                  {/* Stats Section */}
                  <Box 
                    sx={{ 
                      mt: 0,
                      pt: 2.5,
                      borderTop: '2px solid #f1f5f9',
                    }}
                  >
                    {/* Tasks */}
                    <Box sx={{ mb: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Tasks
                        </Typography>
                        <Typography variant="h6" sx={{ color: colors.main, fontWeight: 800, fontSize: '1.1rem' }}>
                          {performer.achievements}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(performer.achievements / 156) * 100}
                        sx={{
                          height: 7,
                          borderRadius: 4,
                          bgcolor: '#f1f5f9',
                          '& .MuiLinearProgress-bar': {
                            background: `linear-gradient(90deg, ${colors.main} 0%, ${colors.dark} 100%)`,
                            borderRadius: 4,
                            boxShadow: `0 2px 8px ${colors.shadow}`,
                          },
                        }}
                      />
                    </Box>

                    {/* Growth */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        borderRadius: 2.5,
                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                        border: '1.5px solid #a7f3d0',
                        boxShadow: '0 2px 8px rgba(5, 150, 105, 0.1)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <TrendingUpIcon sx={{ fontSize: 18, color: '#059669' }} />
                        <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Growth
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669', fontSize: '1.1rem' }}>
                        {performer.growth}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default TopPerformersSection;